import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  Skeleton,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People,
  MedicalServices,
  Person,
  Search,
  TrendingUp,
  SearchOff,
  EmojiEvents,
  Sync,
} from '@mui/icons-material';
import apiClient from '../lib/axios';

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

function formatSyncDate(iso: string | null): string {
  if (!iso) return 'Nunca';
  return new Date(iso).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
}

function relativeSyncTime(iso: string | null): string {
  if (!iso) return '';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

const StatCard: React.FC<{
  value: number | undefined;
  label: string;
  icon: React.ReactNode;
  loading: boolean;
}> = ({ value, label, icon, loading }) => (
  <Card
    elevation={0}
    sx={{
      flex: '0 0 140px',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    }}
  >
    <Box sx={{ color: BLUE }}>{icon}</Box>
    {loading ? (
      <Skeleton width={60} height={36} />
    ) : (
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: NAVY, lineHeight: 1 }}>
        {value?.toLocaleString() ?? '—'}
      </Typography>
    )}
    <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
      {label}
    </Typography>
  </Card>
);

const KpiCard: React.FC<{
  value: string | number | undefined;
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  color: string;
  loading: boolean;
}> = ({ value, label, sublabel, icon, color, loading }) => (
  <Card
    elevation={0}
    sx={{
      flex: '1 1 160px',
      minWidth: 140,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      p: 2.5,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
    </Box>
    {loading ? (
      <Skeleton width={80} height={32} />
    ) : (
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: NAVY, lineHeight: 1 }}>
        {value ?? '—'}
      </Typography>
    )}
    {sublabel && (
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>{sublabel}</Typography>
    )}
  </Card>
);

export const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const { data } = await apiClient.get('/admin/stats');
      return data;
    },
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: async () => {
      const { data } = await apiClient.get('/admin/analytics');
      return data as {
        totalSearches30d: number;
        zeroResultRate: number;
        topSearches: { query: string; count: number }[];
        zeroResultSearches: { query: string; count: number }[];
        topDoctors: { userId: string; displayName: string; searchCount: number; lastActive: string | null }[];
        searchVolumeByDay: { date: string; count: number }[];
      };
    },
  });

  const queryClient = useQueryClient();

  const { data: catalogStats, isLoading: catalogLoading } = useQuery({
    queryKey: ['admin-catalog-stats'],
    queryFn: async () => {
      const { data } = await apiClient.get('/products/catalog-stats');
      return data as {
        lastSyncedAt: string | null;
        totalProducts: number;
        totalInStock: number;
        totalBranches: number;
      };
    },
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      // 1) real ERP delta sync — writes pharmacy_products.lastSyncedAt
      await apiClient.post('/pharmacy-products/sync');
      // 2) recompute the dashboard stats cache so the card reflects the new time
      await apiClient.post('/products/catalog-stats/refresh');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-catalog-stats'] });
    },
  });

  // Compute max for bar chart
  const maxVolume = analytics?.searchVolumeByDay?.length
    ? Math.max(...analytics.searchVolumeByDay.map((d) => d.count), 1)
    : 1;

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.12)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.08)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.03)', top: '20%', right: '20%', pointerEvents: 'none' }} />

        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', mb: 0.75 }}>
          SUPER ADMIN
        </Typography>
        <Typography variant="h2" sx={{ color: '#fff', mb: 0.75 }}>
          {t('admin.dashboard.systemOverview')}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
          Gestión completa del sistema Lysi-Web
        </Typography>
      </Box>

      {/* User stats row */}
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '1.5px', textTransform: 'uppercase', mb: 1.5 }}>
        Usuarios del sistema
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 0.5,
          mb: 3,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <StatCard value={stats?.totalUsers} label={t('admin.dashboard.totalUsers')} icon={<People />} loading={statsLoading} />
        <StatCard value={stats?.activeUsers} label={t('admin.dashboard.activeUsers')} icon={<Person />} loading={statsLoading} />
        <StatCard value={stats?.usersByRole?.DOCTOR ?? 0} label={t('admin.dashboard.doctors')} icon={<MedicalServices />} loading={statsLoading} />
      </Box>

      {/* Catalog sync */}
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '1.5px', textTransform: 'uppercase', mb: 1.5 }}>
        Catálogo de productos
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Card
          elevation={0}
          sx={{
            flex: '1 1 280px',
            minWidth: 240,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: `${BLUE}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: BLUE, flexShrink: 0 }}>
              <Sync sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
              Última sincronización
            </Typography>
          </Box>
          {catalogLoading ? (
            <Skeleton width={160} height={32} />
          ) : (
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: NAVY, lineHeight: 1.1 }}>
              {formatSyncDate(catalogStats?.lastSyncedAt ?? null)}
            </Typography>
          )}
          {!catalogLoading && catalogStats?.lastSyncedAt && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
              {relativeSyncTime(catalogStats.lastSyncedAt)}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            disabled={syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
            startIcon={syncMutation.isPending ? <CircularProgress size={14} color="inherit" /> : <Sync sx={{ fontSize: 16 }} />}
            sx={{ mt: 2, borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
          >
            {syncMutation.isPending ? 'Sincronizando…' : 'Sincronizar ahora'}
          </Button>
          {syncMutation.isError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              No se pudo sincronizar. Intenta de nuevo.
            </Alert>
          )}
        </Card>
      </Box>

      {/* Search KPIs */}
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '1.5px', textTransform: 'uppercase', mb: 1.5 }}>
        Actividad de búsqueda — últimos 30 días
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <KpiCard
          value={analytics?.totalSearches30d?.toLocaleString()}
          label="Búsquedas totales"
          sublabel="en los últimos 30 días"
          icon={<Search sx={{ fontSize: 16 }} />}
          color={BLUE}
          loading={analyticsLoading}
        />
        <KpiCard
          value={analytics ? `${analytics.zeroResultRate}%` : undefined}
          label="Sin resultados"
          sublabel="tasa de 0 resultados"
          icon={<SearchOff sx={{ fontSize: 16 }} />}
          color={analytics && analytics.zeroResultRate > 20 ? '#EF4444' : '#F59E0B'}
          loading={analyticsLoading}
        />
        <KpiCard
          value={analytics?.topDoctors?.[0]?.searchCount?.toLocaleString()}
          label="Top doctor"
          sublabel={analytics?.topDoctors?.[0]?.displayName}
          icon={<EmojiEvents sx={{ fontSize: 16 }} />}
          color="#10B981"
          loading={analyticsLoading}
        />
        <KpiCard
          value={analytics?.searchVolumeByDay?.length ?? 0}
          label="Días con actividad"
          sublabel="con al menos 1 búsqueda"
          icon={<TrendingUp sx={{ fontSize: 16 }} />}
          color="#7C3AED"
          loading={analyticsLoading}
        />
      </Box>

      {/* Two-column analytics grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        {/* Top searched medications */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 2.5 } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mb: 0.5 }}>Medicamentos más buscados</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>Términos con resultados — 30 días</Typography>
          {analyticsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(5)].map((_, i) => <Skeleton key={i} height={28} />)}
            </Box>
          ) : analytics?.topSearches?.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {analytics.topSearches.map((item, idx) => (
                <Box key={item.query} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', width: 18, textAlign: 'right', flexShrink: 0 }}>
                    {idx + 1}
                  </Typography>
                  <Box sx={{ flex: 1, position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        borderRadius: 1,
                        bgcolor: `${BLUE}12`,
                        width: `${Math.max(8, (item.count / (analytics.topSearches[0]?.count ?? 1)) * 100)}%`,
                        transition: 'width 0.4s ease',
                      }}
                    />
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>{item.query}</Typography>
                      <Chip label={item.count} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: `${BLUE}18`, color: BLUE, '& .MuiChip-label': { px: 0.75 } }} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2, textAlign: 'center' }}>Sin datos aún</Typography>
          )}
        </Card>

        {/* Zero-result searches */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 2.5 } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mb: 0.5 }}>Búsquedas sin resultados</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>Términos no encontrados — 30 días</Typography>
          {analyticsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(5)].map((_, i) => <Skeleton key={i} height={28} />)}
            </Box>
          ) : analytics?.zeroResultSearches?.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {analytics.zeroResultSearches.map((item, idx) => (
                <Box key={item.query} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', width: 18, textAlign: 'right', flexShrink: 0 }}>
                    {idx + 1}
                  </Typography>
                  <Box sx={{ flex: 1, position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        borderRadius: 1,
                        bgcolor: 'rgba(239,68,68,0.08)',
                        width: `${Math.max(8, (item.count / (analytics.zeroResultSearches[0]?.count ?? 1)) * 100)}%`,
                        transition: 'width 0.4s ease',
                      }}
                    />
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>{item.query}</Typography>
                      <Chip label={item.count} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(239,68,68,0.1)', color: '#EF4444', '& .MuiChip-label': { px: 0.75 } }} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2, textAlign: 'center' }}>Sin búsquedas fallidas</Typography>
          )}
        </Card>

        {/* Top doctors */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 2.5 } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mb: 0.5 }}>Doctores más activos</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>Por volumen de búsquedas — 30 días</Typography>
          {analyticsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(5)].map((_, i) => <Skeleton key={i} height={36} />)}
            </Box>
          ) : analytics?.topDoctors?.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {analytics.topDoctors.map((doc, idx) => (
                <Box key={doc.userId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 2, bgcolor: idx === 0 ? 'rgba(16,185,129,0.05)' : 'transparent', border: idx === 0 ? '1px solid rgba(16,185,129,0.15)' : '1px solid transparent' }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: idx === 0 ? 'rgba(16,185,129,0.15)' : `${BLUE}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: idx === 0 ? '#10B981' : BLUE }}>{idx + 1}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doc.displayName}
                    </Typography>
                    {doc.lastActive && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        Últ. búsqueda {new Date(doc.lastActive).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={`${doc.searchCount} búsq.`} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, flexShrink: 0 }} />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2, textAlign: 'center' }}>Sin actividad registrada</Typography>
          )}
        </Card>

        {/* Daily volume chart */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 2.5 } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mb: 0.5 }}>Volumen diario de búsquedas</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>Últimos 30 días</Typography>
          {analyticsLoading ? (
            <Skeleton height={120} variant="rectangular" sx={{ borderRadius: 2 }} />
          ) : analytics?.searchVolumeByDay?.length ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 100, mt: 1 }}>
              {analytics.searchVolumeByDay.map((day) => (
                <Box
                  key={day.date}
                  title={`${day.date}: ${day.count}`}
                  sx={{
                    flex: 1,
                    minWidth: 4,
                    borderRadius: '3px 3px 0 0',
                    bgcolor: BLUE,
                    opacity: 0.7 + (day.count / maxVolume) * 0.3,
                    height: `${Math.max(4, (day.count / maxVolume) * 100)}%`,
                    transition: 'height 0.3s ease',
                    cursor: 'default',
                    '&:hover': { opacity: 1, bgcolor: '#0052a3' },
                  }}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Sin datos aún</Typography>
            </Box>
          )}
          {analytics?.searchVolumeByDay?.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                {analytics.searchVolumeByDay[0]?.date}
              </Typography>
              <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                {analytics.searchVolumeByDay[analytics.searchVolumeByDay.length - 1]?.date}
              </Typography>
            </Box>
          ) : null}
        </Card>
      </Box>
    </Box>
  );
};
